export interface UnitDef { name: string; toBase: (v: number) => number; fromBase: (b: number) => number }
export interface Category {
  id: string; label: string; units: UnitDef[];
  /** True when negative values are meaningful (temperature, angle). The value field then keeps a keyboard that can type a minus sign. */
  negative?: boolean;
}

const lin = (name: string, f: number): UnitDef => ({ name, toBase: (v) => v * f, fromBase: (b) => b / f });
const make = (id: string, label: string, list: [string, number][]): Category => ({ id, label, units: list.map(([n, f]) => lin(n, f)) });

export const CATEGORIES: Category[] = [
  make("length", "Length", [
    ["meter", 1], ["kilometer", 1000], ["centimeter", 0.01], ["millimeter", 0.001], ["micrometer", 1e-6], ["nanometer", 1e-9],
    ["mile", 1609.344], ["yard", 0.9144], ["foot", 0.3048], ["inch", 0.0254], ["nautical mile", 1852],
  ]),
  make("weight", "Weight", [
    ["kilogram", 1], ["gram", 0.001], ["milligram", 1e-6], ["microgram", 1e-9], ["tonne (metric, 1000 kg)", 1000],
    ["pound", 0.45359237], ["ounce", 0.028349523125], ["stone (UK)", 6.35029318], ["US ton (short, 2000 lb)", 907.18474], ["UK ton (long, 2240 lb)", 1016.0469088],
  ]),
  make("volume", "Volume", [
    ["liter", 1], ["milliliter", 0.001], ["cubic meter", 1000], ["cubic centimeter", 0.001],
    ["US gallon", 3.785411784], ["US quart", 0.946352946], ["US pint", 0.473176473], ["US cup", 0.2365882365], ["US fluid ounce", 0.0295735295625], ["US tablespoon", 0.01478676478125], ["US teaspoon", 0.00492892159375],
    ["UK gallon", 4.54609], ["UK quart", 1.1365225], ["UK pint", 0.56826125], ["UK fluid ounce", 0.0284130625],
    ["cubic foot", 28.316846592], ["cubic inch", 0.016387064],
  ]),
  make("area", "Area", [
    ["sq meter", 1], ["sq kilometer", 1e6], ["sq centimeter", 1e-4], ["sq millimeter", 1e-6], ["hectare", 1e4],
    ["acre", 4046.8564224], ["sq mile", 2589988.110336], ["sq yard", 0.83612736], ["sq foot", 0.09290304], ["sq inch", 0.00064516],
  ]),
  {
    id: "temperature", label: "Temperature", negative: true,
    units: [
      { name: "celsius", toBase: (v) => v, fromBase: (b) => b },
      { name: "fahrenheit", toBase: (v) => ((v - 32) * 5) / 9, fromBase: (b) => (b * 9) / 5 + 32 },
      { name: "kelvin", toBase: (v) => v - 273.15, fromBase: (b) => b + 273.15 },
      { name: "rankine", toBase: (v) => ((v - 491.67) * 5) / 9, fromBase: (b) => ((b + 273.15) * 9) / 5 },
    ],
  },
  make("speed", "Speed", [["meter/second", 1], ["kilometer/hour", 1 / 3.6], ["mile/hour", 0.44704], ["knot", 1852 / 3600], ["foot/second", 0.3048]]),
  make("time", "Time", [["second", 1], ["millisecond", 0.001], ["minute", 60], ["hour", 3600], ["day", 86400], ["week", 604800], ["year (365.25 days)", 31557600]]),
  make("data", "Data", [
    ["bit", 0.125], ["byte", 1], ["kilobyte (1000 B)", 1e3], ["megabyte (1000² B)", 1e6], ["gigabyte (1000³ B)", 1e9], ["terabyte (1000⁴ B)", 1e12],
    ["kibibyte (1024 B)", 1024], ["mebibyte (1024² B)", 1048576], ["gibibyte (1024³ B)", 1073741824], ["tebibyte (1024⁴ B)", 1099511627776],
  ]),
  make("pressure", "Pressure", [["pascal", 1], ["kilopascal", 1000], ["bar", 1e5], ["psi", 6894.757293168], ["atmosphere", 101325], ["mmHg", 133.322387415]]),
  make("energy", "Energy", [["joule", 1], ["kilojoule", 1000], ["calorie (small)", 4.184], ["kilocalorie (food Calorie)", 4184], ["watt-hour", 3600], ["kilowatt-hour", 3.6e6], ["BTU", 1055.05585262], ["foot-pound", 1.3558179483314004]]),
  // Power. Base unit: watt.
  make("power", "Power", [
    ["watt", 1], ["kilowatt", 1000], ["megawatt", 1e6],
    // Mechanical (imperial) horsepower is defined as 33,000 ft·lbf per minute. With the exact foot (0.3048 m), pound (0.45359237 kg)
    // and standard gravity (9.80665 m/s²) that is 745.6998715822702 W.
    ["horsepower (mechanical)", (33000 * 0.3048 * 0.45359237 * 9.80665) / 60],
    // Metric horsepower (PS, CV, pk) is defined as 75 kgf·m/s = 75 × 9.80665 = 735.49875 W (DIN 66036).
    ["horsepower (metric)", 75 * 9.80665],
    // BTU/h uses the International Table BTU (1055.05585262 J), the same BTU as the Energy tab, divided by 3600 s.
    ["BTU/hour", 1055.05585262 / 3600],
  ]),
  // Angle. Base unit: degree. The full turn is 360 degrees by definition; 1 gradian = 1/400 turn = 0.9 degree;
  // 1 arcminute = 1/60 degree; 1 arcsecond = 1/3600 degree; 1 radian = 180/π degrees (SI brochure, 9th edition).
  { ...make("angle", "Angle", [
    ["degree", 1], ["radian", 180 / Math.PI], ["gradian", 0.9], ["arcminute", 1 / 60], ["arcsecond", 1 / 3600], ["turn (revolution)", 360],
  ]), negative: true },
  // Frequency. Base unit: hertz (1 Hz = 1 cycle per second). Revolutions per minute is 1/60 Hz by definition.
  make("frequency", "Frequency", [
    ["hertz", 1], ["kilohertz", 1e3], ["megahertz", 1e6], ["gigahertz", 1e9], ["revolutions/minute (rpm)", 1 / 60],
  ]),
  {
    // Base unit is liters per 100 km; the others are reciprocals of it.
    id: "fuel", label: "Fuel economy",
    units: [
      { name: "liters/100 km", toBase: (v) => v, fromBase: (b) => b },
      { name: "km/liter", toBase: (v) => 100 / v, fromBase: (b) => 100 / b },
      { name: "miles/gallon (US)", toBase: (v) => 235.214583333 / v, fromBase: (b) => 235.214583333 / b },
      { name: "miles/gallon (UK)", toBase: (v) => 282.480936331 / v, fromBase: (b) => 282.480936331 / b },
    ],
  },
];

/**
 * Cooking: US kitchen volumes <-> grams for a short list of common ingredients. APPROXIMATE by nature: how a cup is filled
 * (spooned or scooped, sifted, packed) and the brand change the weight, so the grams are typical published values, not exact.
 * Volume units use the exact US definitions (cup = 236.5882365 mL, tablespoon = cup/16, teaspoon = cup/48).
 *
 * gramsPerCup sources (grams in one US cup):
 *  - water 236.588: 1 g/mL (density of water near 4 degrees C; about 0.998 g/mL at room temperature, a rounding of 0.2%).
 *  - milk, whole 244: USDA FoodData Central, SR Legacy, household measure "1 cup" (244 g).
 *  - vegetable oil 218: USDA FoodData Central, SR Legacy, household measure "1 cup" (218 g).
 *  - honey 339: USDA FoodData Central, SR Legacy, household measure "1 cup" (339 g).
 *  - butter 227: USDA FoodData Central and King Arthur Baking ingredient weight chart agree (2 sticks = 227 g).
 *  - sugar, granulated 200: USDA 200 g; King Arthur lists 198 g. 200 is used.
 *  - sugar, brown packed 213: King Arthur Baking ingredient weight chart (213 g, packed); USDA lists about 220 g.
 *  - sugar, powdered 120: USDA 120 g (unsifted); King Arthur lists about 113 g.
 *  - flour, all-purpose 120: King Arthur Baking ingredient weight chart (spooned and leveled, 120 g); USDA lists 125 g, and
 *    scooping straight from the bag can give 140 g or more.
 * NOTE: the figures were entered from those published tables without being re-opened from the build environment (no network);
 * an owner or reviewer should spot-check them against the live USDA and King Arthur pages.
 */
export const COOKING_INGREDIENTS: { id: string; name: string; gramsPerCup: number }[] = [
  { id: "flour", name: "all-purpose flour", gramsPerCup: 120 },
  { id: "sugar", name: "granulated sugar", gramsPerCup: 200 },
  { id: "brown-sugar", name: "brown sugar (packed)", gramsPerCup: 213 },
  { id: "powdered-sugar", name: "powdered sugar", gramsPerCup: 120 },
  { id: "butter", name: "butter", gramsPerCup: 227 },
  { id: "honey", name: "honey", gramsPerCup: 339 },
  { id: "milk", name: "whole milk", gramsPerCup: 244 },
  { id: "oil", name: "vegetable oil", gramsPerCup: 218 },
  { id: "water", name: "water", gramsPerCup: 236.5882365 },
];

const ML_PER_CUP = 236.5882365;

/** Cooking category for one ingredient. Base unit: milliliter; grams go through the ingredient's density (g per mL). */
export function cookingCategory(ingredientId: string = COOKING_INGREDIENTS[0].id): Category {
  const ing = COOKING_INGREDIENTS.find((i) => i.id === ingredientId) ?? COOKING_INGREDIENTS[0];
  const density = ing.gramsPerCup / ML_PER_CUP;
  const vol = (name: string, ml: number) => lin(name, ml);
  const mass = (name: string, grams: number): UnitDef => ({ name, toBase: (v) => (v * grams) / density, fromBase: (b) => (b * density) / grams });
  return {
    id: "cooking", label: "Cooking (approx.)",
    units: [
      vol("US cup", ML_PER_CUP), vol("US tablespoon", ML_PER_CUP / 16), vol("US teaspoon", ML_PER_CUP / 48), vol("milliliter", 1),
      mass("gram", 1), mass("ounce (weight)", 28.349523125),
    ],
  };
}

CATEGORIES.push(cookingCategory());

export function convertValue(cat: Category, from: string, to: string, v: number): number {
  const f = cat.units.find((u) => u.name === from), t = cat.units.find((u) => u.name === to);
  if (!f || !t) return NaN;
  return t.fromBase(f.toBase(v));
}

/** Up to 10 significant digits, no trailing zeros, thousands separators, scientific notation for very small/large values. */
export function formatNumber(n: number, locale?: string): string {
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs < 1e-6 || abs >= 1e15) return n.toExponential(6).replace(/\.?0+e/, "e").replace("e+", "e");
  return new Intl.NumberFormat(locale, { maximumSignificantDigits: 10 }).format(Number(n.toPrecision(10)));
}
